'use client';

import posthog from 'posthog-js';
import { PostHogClientConfig } from '@/lib/data/types/analytics';

/**
 * PostHog client configuration and initialization
 * Following NextJS 15 best practices for client-side analytics
 */

class PostHogClient {
  private static instance: PostHogClient;
  private isInitialized = false;
  private config: PostHogClientConfig | null = null;

  private constructor() {}

  public static getInstance(): PostHogClient {
    if (!PostHogClient.instance) {
      PostHogClient.instance = new PostHogClient();
    }
    return PostHogClient.instance;
  }

  /**
   * Initialize PostHog client with configuration
   * Only runs on client-side with proper environment variables
   */
  public initialize(): void {
    // Only initialize on client-side
    if (typeof window === 'undefined') {
      return;
    }

    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }

    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    
    // Use reverse proxy to avoid ad blockers
    // Vercel rewrites /api/events/* -> https://eu.i.posthog.com/*
    // Path chosen to appear as normal API endpoint rather than analytics tracking
    // Avoids obvious patterns like /analytics, /tracking, /posthog that get blocked
    const apiHost = '/api/events';

    if (!apiKey) {
      console.warn('PostHog: API key not found. Analytics disabled.');
      return;
    }

    this.config = {
      apiKey,
      apiHost,
      autocapture: true,
      capture_pageview: true, // Enable PostHog's automatic $pageview tracking
      session_recording: process.env.NODE_ENV === 'production',
      disable_session_recording: process.env.NODE_ENV !== 'production',
      debug: process.env.NODE_ENV === 'development', // Enable debug mode in development
      loaded: (ph) => {
        // Set up user identification and session tracking
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog loaded successfully');
        }
      }
    };

          try {
        posthog.init(this.config.apiKey, {
          api_host: this.config.apiHost,
          ui_host: 'https://eu.posthog.com', // Set UI host when using proxy
          person_profiles: 'identified_only',
          autocapture: this.config.autocapture,
          capture_pageview: this.config.capture_pageview,
          disable_session_recording: this.config.disable_session_recording,
          debug: this.config.debug,
          loaded: this.config.loaded,
          // Privacy settings
          respect_dnt: true,
          opt_out_capturing_by_default: false,
        });

      this.isInitialized = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog initialized successfully');
      }
    } catch (error) {
      console.error('PostHog initialization failed:', error);
    }
  }

  /**
   * Get the PostHog instance
   * Returns null if not initialized or on server-side
   */
  public getClient(): typeof posthog | null {
    if (typeof window === 'undefined' || !this.isInitialized) {
      return null;
    }
    return posthog;
  }

  /**
   * Check if PostHog is ready for tracking
   */
  public isReady(): boolean {
    return this.isInitialized && typeof window !== 'undefined';
  }

  /**
   * Identify user for analytics
   * @param userId - Unique user identifier
   * @param properties - User properties
   */
  public identify(userId: string, properties?: Record<string, any>): void {
    const client = this.getClient();
    if (!client) return;

    client.identify(userId, properties);
  }

  /**
   * Reset user identification (e.g., on logout)
   */
  public reset(): void {
    const client = this.getClient();
    if (!client) return;

    client.reset();
  }

  /**
   * Capture a custom event
   * @param event - Event name
   * @param properties - Event properties
   */
  public capture(event: string, properties?: Record<string, any>): void {
    const client = this.getClient();
    if (!client) return;

    client.capture(event, {
      ...properties,
      timestamp: Date.now(),
      $current_url: window.location.href,
      $referrer: document.referrer || undefined,
    });
  }

  /**
   * Set user properties
   * @param properties - Properties to set
   */
  public setPersonProperties(properties: Record<string, any>): void {
    const client = this.getClient();
    if (!client) return;

    client.setPersonProperties(properties);
  }

  /**
   * Get session ID for tracking
   */
  public getSessionId(): string {
    const client = this.getClient();
    if (!client) return 'no-session';

    return client.get_session_id() || 'no-session';
  }

  /**
   * Get distinct ID for tracking
   */
  public getDistinctId(): string {
    const client = this.getClient();
    if (!client) return 'anonymous';

    return client.get_distinct_id() || 'anonymous';
  }

  /**
   * Enable or disable analytics
   * @param enabled - Whether to enable analytics
   */
  public setEnabled(enabled: boolean): void {
    const client = this.getClient();
    if (!client) return;

    if (enabled) {
      client.opt_in_capturing();
    } else {
      client.opt_out_capturing();
    }
  }
}

// Export singleton instance
export const posthogClient = PostHogClient.getInstance();

// Convenience functions for direct usage
export const initializePostHog = () => posthogClient.initialize();
export const captureAnalyticsEvent = (event: string, properties?: Record<string, any>) => 
  posthogClient.capture(event, properties);
export const identifyUser = (userId: string, properties?: Record<string, any>) => 
  posthogClient.identify(userId, properties);
export const resetUser = () => posthogClient.reset();
export const getAnalyticsSessionId = () => posthogClient.getSessionId();
export const getAnalyticsUserId = () => posthogClient.getDistinctId();
export const setAnalyticsEnabled = (enabled: boolean) => posthogClient.setEnabled(enabled); 